import React, { useState, useRef, useEffect } from 'react';
import { API, COLORS } from '../utils/constants';
import Popup from './Popup';
import ImagePicker from './ImagePicker';
// Helper to clean decimal input - preserves "0." at start
const cleanDecimal = (v) => {
  if (!v) return '';
  let cleaned = v.replace(/[^0-9.,]/g, '');
  if (cleaned.match(/^0+[.,]/)) cleaned = cleaned.replace(/^0+/, '0');
  cleaned = cleaned.replace(',', '.');
  const parts = cleaned.split('.');
  if (parts.length > 2) cleaned = parts[0] + '.' + parts.slice(1).join('');
  return cleaned;
};


export default function AlimentoWizard({ onClose, onComplete, modoNutricion = false, initialNombre = "", initialData = null, zIndex = 600 }) {
  const [tipoMedida, setTipoMedida] = useState(initialData?.tipo_medida || "peso");
  const [pesoUnidad, setPesoUnidad] = useState(initialData?.peso_unidad ? String(initialData.peso_unidad) : "");
  const [densidad, setDensidad] = useState("1.0");
  const [form, setForm] = useState({
    nombre: initialData?.nombre || initialNombre || "",
    calorias: initialData?.calorias ? String(initialData.calorias) : "",
    proteina: initialData?.proteina ? String(initialData.proteina) : "",
    carbos: initialData?.carbos ? String(initialData.carbos) : "",
    grasa: initialData?.grasa ? String(initialData.grasa) : "",
  });
  const [cantidad, setCantidad] = useState("");
  const [fromScan, setFromScan] = useState(initialData !== null);
  const [saveToDb, setSaveToDb] = useState(true);

  // Búsqueda por nombre
  const [nombreResults, setNombreResults] = useState([]);
  const [showNombreResults, setShowNombreResults] = useState(false);
  const nombreSearchTimeout = useRef(null);

  const cantidadLabel = tipoMedida === "liquido" ? "ml consumidos" : tipoMedida === "unidad" ? "unidades consumidas" : "gramos consumidos";

  const canSave = !!form.nombre.trim() &&
    (tipoMedida === "peso" || tipoMedida === "liquido" || (tipoMedida === "unidad" && !!pesoUnidad));

  const searchNombre = async (q) => {
    if (q.length < 2) { setNombreResults([]); setShowNombreResults(false); return; }
    try {
      const [local, base] = await Promise.all([
        fetch(API + `/alimentos/buscar?q=${encodeURIComponent(q)}`, { credentials: "include" }).then(r => r.json()).catch(() => []),
        fetch(API + `/alimentos/local?q=${encodeURIComponent(q)}`, { credentials: "include" }).then(r => r.json()).catch(() => []),
      ]);
      const merged = [...(local||[]), ...(base||[])].filter((v,i,a) => a.findIndex(t => t.nombre === v.nombre) === i).slice(0, 5);
      setNombreResults(merged);
      setShowNombreResults(true);
    } catch { setNombreResults([]); setShowNombreResults(true); }
  };

  const handleNombreChange = (val) => {
    setForm(p => ({ ...p, nombre: val }));
    clearTimeout(nombreSearchTimeout.current);
    if (val.length >= 2) {
      nombreSearchTimeout.current = setTimeout(() => searchNombre(val), 350);
    } else {
      setNombreResults([]); setShowNombreResults(false);
    }
  };

  const selectFromSearch = (s) => {
    setForm({ nombre: s.nombre, calorias: String(s.calorias||""), proteina: String(s.proteina||""), carbos: String(s.carbos||""), grasa: String(s.grasa||"") });
    if (s.tipo_medida) setTipoMedida(s.tipo_medida);
    if (s.peso_unidad) setPesoUnidad(String(s.peso_unidad));
    setNombreResults([]); setShowNombreResults(false);
  };

  const searchOnline = async () => {
    setShowNombreResults(false);
    try {
      const res = await fetch(API + `/alimentos/online?q=${encodeURIComponent(form.nombre)}`, { credentials: "include" }).then(r => r.json());
      const first = Array.isArray(res) && res.length > 0 ? res[0] : null;
      if (first && first.calorias) {
        setForm({ nombre: first.nombre || form.nombre, calorias: String(first.calorias||""), proteina: String(first.proteina||""), carbos: String(first.carbos||""), grasa: String(first.grasa||"") });
        setFromScan(true);
      } else {
        const claude = await fetch(API + "/alimentos/buscar-nombre", { method: "POST", headers: {"Content-Type":"application/json"}, credentials:"include", body: JSON.stringify({ nombre: form.nombre }) }).then(r => r.json()).catch(() => null);
        if (claude && claude.calorias !== undefined) {
          setForm({ nombre: claude.nombre || form.nombre, calorias: String(claude.calorias||""), proteina: String(claude.proteina||""), carbos: String(claude.carbos||""), grasa: String(claude.grasa||"") });
          setFromScan(true);
        }
      }
    } catch {}
  };

  const handleSave = async () => {
    const data = {
      nombre: form.nombre.trim(),
      calorias: parseFloat(form.calorias) || 0,
      proteina: parseFloat(form.proteina) || 0,
      carbos: parseFloat(form.carbos) || 0,
      grasa: parseFloat(form.grasa) || 0,
      tipo_medida: tipoMedida,
      tipo_cantidad: tipoMedida === "unidad" ? "unidad" : "peso",
      peso_unidad: tipoMedida === "unidad" ? (parseFloat(pesoUnidad) || null) : null,
      densidad: tipoMedida === "liquido" ? (parseFloat(densidad) || 1.0) : 1.0,
      cantidad: parseFloat(cantidad) || (tipoMedida === "unidad" ? 1 : 100),
    };
    if (fromScan && saveToDb) {
      await fetch(API + "/alimentos", {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ nombre: data.nombre, calorias: data.calorias, proteina: data.proteina, carbos: data.carbos, grasa: data.grasa, fuente: "scan", tipo_medida: tipoMedida, peso_unidad: data.peso_unidad, densidad: data.densidad }),
      }).catch(() => {});
    }
    onComplete(data);
  };

  return (
    <Popup onClose={onClose} zIndex={zIndex}>

        {/* Header con botón cámara */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <span style={{ color: COLORS.text, fontSize: 18, fontWeight: 800 }}>Nuevo alimento</span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <ImagePicker
              endpoint="/scan-label"
              title="Etiqueta nutricional"
              description="Escanea la etiqueta del producto para rellenar los datos automáticamente."
              onResult={(data) => {
                if (data.nombre) setForm(p => ({ ...p, nombre: data.nombre }));
                if (data.calorias !== undefined) setForm(p => ({ ...p, calorias: String(Math.round(data.calorias * 10) / 10) }));
                if (data.proteina !== undefined) setForm(p => ({ ...p, proteina: String(Math.round(data.proteina * 10) / 10) }));
                if (data.carbos !== undefined) setForm(p => ({ ...p, carbos: String(Math.round(data.carbos * 10) / 10) }));
                if (data.grasa !== undefined) setForm(p => ({ ...p, grasa: String(Math.round(data.grasa * 10) / 10) }));
                if (data.tipo_medida) setTipoMedida(data.tipo_medida);
                if (data.peso_unidad) setPesoUnidad(String(data.peso_unidad));
                if (data.cantidad_sugerida) setCantidad(String(data.cantidad_sugerida));
                setFromScan(true);
                setSaveToDb(true);
              }}
              onError={() => {}}
            >
              {(triggerOpen, loading) => (
                <button
                  onClick={triggerOpen}
                  disabled={loading}
                  style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: "6px 11px", color: loading ? "rgba(255,255,255,0.3)" : COLORS.accent, fontSize: 18, cursor: loading ? "default" : "pointer", lineHeight: 1 }}
                >
                  {loading ? "⏳" : "📷"}
                </button>
              )}
            </ImagePicker>
            <button onClick={onClose} style={{ background: "none", border: "none", color: COLORS.textMuted, fontSize: 24, cursor: "pointer", lineHeight: 1, padding: 0 }}>✕</button>
          </div>
        </div>

        {/* Segmented control tipo de medida */}
        <div style={{ display: "flex", gap: 6, marginBottom: 20, background: COLORS.bg, borderRadius: 12, padding: 4 }}>
          {[
            { val: "peso", icon: "⚖️", label: "Gramos" },
            { val: "unidad", icon: "🥚", label: "Unidad" },
            { val: "liquido", icon: "💧", label: "ml" },
          ].map(({ val, icon, label }) => (
            <button key={val} onClick={() => setTipoMedida(val)} style={{ flex: 1, padding: "9px 4px", borderRadius: 9, border: "none", background: tipoMedida === val ? COLORS.accent : "transparent", color: tipoMedida === val ? "#fff" : COLORS.textMuted, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
              <span style={{ fontSize: 16 }}>{icon}</span>
              <span>{label}</span>
            </button>
          ))}
        </div>

        {/* Campos extra según tipo */}
        {tipoMedida === "unidad" && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ color: COLORS.textMuted, fontSize: 10, letterSpacing: 1, display: "block", marginBottom: 6 }}>GRAMOS POR UNIDAD</label>
            <input type="text" inputMode="decimal" value={pesoUnidad} onChange={e => setPesoUnidad(cleanDecimal(e.target.value))} placeholder="Ej: 60" style={{ width: "100%", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "12px 14px", color: COLORS.text, fontSize: 15, outline: "none", boxSizing: "border-box" }} />
          </div>
        )}
        {tipoMedida === "liquido" && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ color: COLORS.textMuted, fontSize: 10, letterSpacing: 1, display: "block", marginBottom: 6 }}>DENSIDAD (g/ml) — agua 1.0 · aceite 0.92 · leche 1.03</label>
            <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
              {[["Agua", "1.0"], ["Aceite", "0.92"], ["Leche", "1.03"], ["Zumo", "1.04"]].map(([n, v]) => (
                <button key={n} onClick={() => setDensidad(v)} style={{ flex: 1, padding: "7px 2px", borderRadius: 8, border: `1px solid ${densidad === v ? COLORS.teal : COLORS.border}`, background: densidad === v ? COLORS.tealSoft : COLORS.bg, color: densidad === v ? COLORS.teal : COLORS.textMuted, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>{n}</button>
              ))}
            </div>
            <input type="text" inputMode="decimal" value={densidad} onChange={e => setDensidad(cleanDecimal(e.target.value))} style={{ width: "100%", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "12px 14px", color: COLORS.text, fontSize: 15, outline: "none", boxSizing: "border-box" }} />
          </div>
        )}

        {/* Valores nutricionales por 100g */}
        <p style={{ color: COLORS.textMuted, fontSize: 11, letterSpacing: 1, margin: "0 0 12px" }}>VALORES NUTRICIONALES POR 100G</p>

        {/* Campo nombre con búsqueda */}
        <div style={{ marginBottom: 12, position: "relative" }}>
          <label style={{ color: COLORS.textMuted, fontSize: 10, letterSpacing: 1, display: "block", marginBottom: 5 }}>NOMBRE</label>
          <input
            type="text"
            value={form.nombre}
            onChange={e => handleNombreChange(e.target.value)}
            onBlur={() => setTimeout(() => setShowNombreResults(false), 200)}
            style={{ width: "100%", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "11px 13px", color: COLORS.text, fontSize: 15, outline: "none", boxSizing: "border-box" }}
          />
          {showNombreResults && (
            <div style={{ position: "absolute", left: 0, right: 0, zIndex: 10, background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 10, overflow: "hidden", marginTop: 4 }}>
              {nombreResults.map((s, i) => (
                <div key={i} onMouseDown={() => selectFromSearch(s)} style={{ padding: "10px 14px", cursor: "pointer", borderBottom: `1px solid ${COLORS.border}` }}>
                  <span style={{ color: COLORS.text, fontSize: 13 }}>{s.nombre}</span>
                  <span style={{ color: COLORS.textMuted, fontSize: 11, marginLeft: 8 }}>{s.calorias} kcal</span>
                </div>
              ))}
              {form.nombre.length >= 2 && (
                <div onMouseDown={searchOnline} style={{ padding: "10px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, color: COLORS.blue, fontSize: 13, fontWeight: 700 }}>
                  <span>🌐</span><span>Buscar "{form.nombre}" en internet</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Resto de campos numéricos */}
        {[["Calorías (kcal)", "calorias", "number"], ["Proteína (g)", "proteina", "number"], ["Carbos (g)", "carbos", "number"], ["Grasa (g)", "grasa", "number"]].map(([label, key, type]) => (
          <div key={key} style={{ marginBottom: 12 }}>
            <label style={{ color: COLORS.textMuted, fontSize: 10, letterSpacing: 1, display: "block", marginBottom: 5 }}>{label.toUpperCase()}</label>
            <input type={type} value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))} style={{ width: "100%", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "11px 13px", color: COLORS.text, fontSize: 15, outline: "none", boxSizing: "border-box" }} />
          </div>
        ))}

        {modoNutricion && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${COLORS.border}` }}>
            <label style={{ color: COLORS.textMuted, fontSize: 10, letterSpacing: 1, display: "block", marginBottom: 5 }}>{cantidadLabel.toUpperCase()}</label>
            <input type="text" inputMode="decimal" value={cantidad} onChange={e => setCantidad(cleanDecimal(e.target.value))} placeholder={tipoMedida === "unidad" ? "1" : "100"} style={{ width: "100%", background: COLORS.bg, border: `1px solid ${COLORS.accent}40`, borderRadius: 10, padding: "11px 13px", color: COLORS.text, fontSize: 15, outline: "none", boxSizing: "border-box" }} />
          </div>
        )}

        {/* Opción guardar en BD (visible cuando hay datos de scan/internet) */}
        {fromScan && (
          <div
            onClick={() => setSaveToDb(v => !v)}
            style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16, padding: "10px 14px", background: saveToDb ? "rgba(57,255,20,0.07)" : COLORS.bg, border: `1px solid ${saveToDb ? "rgba(57,255,20,0.3)" : COLORS.border}`, borderRadius: 10, cursor: "pointer" }}
          >
            <div style={{ width: 20, height: 20, borderRadius: 6, border: `2px solid ${saveToDb ? "#39FF14" : COLORS.border}`, background: saveToDb ? "#39FF14" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {saveToDb && <span style={{ color: "#000", fontSize: 12, fontWeight: 900, lineHeight: 1 }}>✓</span>}
            </div>
            <span style={{ color: saveToDb ? "#39FF14" : COLORS.textMuted, fontSize: 13, fontWeight: 600 }}>Guardar en base de datos</span>
          </div>
        )}

        <button onClick={handleSave} disabled={!canSave} style={{ width: "100%", marginTop: 16, padding: 16, borderRadius: 12, background: canSave ? `linear-gradient(135deg, ${COLORS.accent}, #e55a2b)` : COLORS.bg, border: canSave ? "none" : `1px solid ${COLORS.border}`, color: canSave ? "#fff" : COLORS.textMuted, cursor: canSave ? "pointer" : "default", fontWeight: 800, fontSize: 15 }}>GUARDAR</button>
    </Popup>
  );
}
